// Iter 335 (Feb 2026), P1 — Backup-Status-Karte für VENDOR_CREDENTIALS_KEY
// ────────────────────────────────────────────────────────────────────────
// Schutz gegen permanenten Datenverlust: Admin muss den Verschlüsselungs-
// Key mindestens einmal gesichert haben. Bis das geschehen ist, zeigt
// die Karte eine rote Warnung. Backup-Bestätigung wird in localStorage
// gespeichert (lokal pro Browser-Profil — ein Admin auf einem anderen
// Gerät sieht die Warnung erneut).
//
// Backend:
//   GET /api/vendor-credentials/backup/status  → Status, Fingerprint
//   GET /api/vendor-credentials/backup/key     → Roher Key (nur Admin)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Copy,
  ShieldAlert,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKUP_FLAG = 'vendor_credentials_key_backed_up_v1';

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export const VendorBackupCard = ({ isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [backupConfirmed, setBackupConfirmed] = useState(
    () => localStorage.getItem(BACKUP_FLAG) === '1'
  );

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendor-credentials/backup/status`, {
        headers: authHeader(),
      });
      setStatus(res.data);
    } catch (e) {
      console.error('Backup status error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleReveal = async () => {
    if (!isAdmin) {
      toast.error('Nur Admin kann den Key sehen');
      return;
    }
    const ok = window.confirm(
      'Achtung: Der nächste Schritt zeigt den Verschlüsselungs-Key im Klartext.\n\n' +
        '• Stelle sicher, dass niemand mitschaut\n' +
        '• Kopiere den Key SOFORT in einen Passwort-Manager\n' +
        '• Diese Aktion wird im Backend-Log vermerkt\n\n' +
        'Fortfahren?'
    );
    if (!ok) return;
    try {
      const res = await axios.get(`${API}/vendor-credentials/backup/key`, {
        headers: authHeader(),
      });
      setRevealedKey(res.data);
      setDialogOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Key konnte nicht geladen werden');
    }
  };

  const handleCopy = async () => {
    if (!revealedKey?.key) return;
    try {
      await navigator.clipboard.writeText(revealedKey.key);
      toast.success('Key in die Zwischenablage kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const handleDownload = () => {
    if (!revealedKey) return;
    const fileContent = {
      app: 'StrongFit Hub',
      purpose: 'Vendor Credentials Encryption Key Backup',
      created_at: revealedKey.revealed_at,
      revealed_to: revealedKey.revealed_to,
      fingerprint: revealedKey.fingerprint,
      key: revealedKey.key,
      env_variable: 'VENDOR_CREDENTIALS_KEY',
      restore_instructions: [
        '1. In /app/backend/.env die Zeile VENDOR_CREDENTIALS_KEY=... mit diesem Key ersetzen.',
        '2. Backend neu starten: sudo supervisorctl restart backend',
        '3. Auf /admin/vendor-credentials prüfen ob Fingerprint mit diesem Backup übereinstimmt.',
      ],
      warning_de:
        'STRENG GEHEIM. Wer diesen Key hat, kann ALLE gespeicherten Lieferanten-Passwörter im Klartext lesen.',
    };
    const blob = new Blob([JSON.stringify(fileContent, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const datestamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `vendor-credentials-key-backup_${datestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Backup-Datei heruntergeladen');
  };

  const handleConfirmBackup = () => {
    localStorage.setItem(BACKUP_FLAG, '1');
    setBackupConfirmed(true);
    setDialogOpen(false);
    setRevealedKey(null);
    toast.success('Backup bestätigt');
  };

  const handleReopenWithoutConfirm = () => {
    setDialogOpen(false);
    setRevealedKey(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-gray-500">
          Backup-Status wird geladen…
        </CardContent>
      </Card>
    );
  }

  if (!status?.configured) {
    return (
      <Card className="border-red-400 bg-red-50">
        <CardContent className="p-4 flex items-center gap-3">
          <ShieldAlert className="text-red-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-semibold text-red-800">
              Kein Verschlüsselungs-Key konfiguriert!
            </div>
            <div className="text-sm text-red-700">
              In <code>backend/.env</code> fehlt der Eintrag{' '}
              <code>VENDOR_CREDENTIALS_KEY=...</code>. Bestell-Logins können
              aktuell nicht gespeichert werden.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status.operational) {
    return (
      <Card className="border-orange-400 bg-orange-50">
        <CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="text-orange-600 shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-semibold text-orange-800">
              Key konfiguriert, aber funktioniert NICHT
            </div>
            <div className="text-sm text-orange-700">
              Encrypt/Decrypt-Roundtrip fehlgeschlagen. Wahrscheinlich falsches
              Format oder Key wurde nach dem ersten Eintrag gewechselt. Bitte
              Backend-Logs prüfen.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const showWarning = !backupConfirmed && status.encrypted_entries > 0;

  return (
    <>
      <Card
        className={
          showWarning
            ? 'border-red-400 bg-red-50'
            : 'border-emerald-300 bg-emerald-50'
        }
        data-testid="vendor-backup-card"
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {showWarning ? (
              <ShieldAlert className="text-red-600 shrink-0 mt-0.5" size={24} />
            ) : (
              <ShieldCheck
                className="text-emerald-600 shrink-0 mt-0.5"
                size={24}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold">
                  {showWarning
                    ? 'Verschlüsselungs-Key noch nicht gesichert!'
                    : backupConfirmed
                      ? 'Key gesichert ✓'
                      : 'Key bereit'}
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    onClick={handleReveal}
                    className={
                      showWarning
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }
                    data-testid="vendor-backup-reveal-btn"
                  >
                    <Eye size={14} className="mr-2" />
                    Key {showWarning ? 'jetzt sichern' : 'erneut anzeigen'}
                  </Button>
                )}
              </div>

              {showWarning && (
                <p className="text-sm text-red-700 mt-1">
                  Es existieren bereits {status.encrypted_entries}{' '}
                  verschlüsselte Einträge. Wird der Key verloren, sind ALLE
                  Lieferanten-Passwörter unwiderruflich nicht mehr lesbar.
                  Klicke auf &bdquo;Key jetzt sichern&ldquo; und speichere ihn
                  in einem Passwort-Manager.
                </p>
              )}

              <div className="flex flex-wrap gap-4 text-xs text-gray-600 mt-2">
                <span>
                  Fingerprint:{' '}
                  <code className="bg-white px-1.5 py-0.5 rounded border">
                    {status.fingerprint}
                  </code>
                </span>
                <span>Einträge verschlüsselt: {status.encrypted_entries}</span>
                {!isAdmin && (
                  <span className="italic">
                    Hinweis: Nur Admins können den Key sichern.
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reveal-Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRevealedKey(null);
          }
          setDialogOpen(open);
        }}
      >
        <DialogContent
          className="max-w-lg"
          data-testid="vendor-backup-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert size={20} />
              Verschlüsselungs-Key
            </DialogTitle>
            <DialogDescription>
              Speichere diesen Key SOFORT in einem Passwort-Manager. Schliesst
              du diesen Dialog ohne ihn zu sichern, ist der Backup unvollständig.
            </DialogDescription>
          </DialogHeader>

          {revealedKey && (
            <div className="space-y-4 py-2">
              <div>
                <div className="text-xs text-gray-500 mb-1">Key</div>
                <div className="bg-gray-100 rounded p-3 font-mono text-sm break-all border-2 border-red-300">
                  {revealedKey.key}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                <div>
                  Fingerprint:{' '}
                  <code className="font-mono">{revealedKey.fingerprint}</code>
                </div>
                <div>
                  Aktion vermerkt für {revealedKey.revealed_to} um{' '}
                  {new Date(revealedKey.revealed_at).toLocaleString('de-CH')}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  data-testid="vendor-backup-copy-btn"
                >
                  <Copy size={14} className="mr-2" />
                  Kopieren
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  data-testid="vendor-backup-download-btn"
                >
                  <Download size={14} className="mr-2" />
                  Als JSON-Datei herunterladen
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
                <p className="font-semibold mb-1 flex items-center gap-1">
                  <AlertTriangle size={14} />
                  Empfohlene Aufbewahrung:
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Passwort-Manager (1Password, Bitwarden, KeePass)</li>
                  <li>
                    Verschlüsselter USB-Stick im Safe / am sicheren Ort
                  </li>
                  <li>Idealerweise an zwei voneinander getrennten Orten</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-row justify-between items-center">
            <Button
              variant="ghost"
              onClick={handleReopenWithoutConfirm}
              data-testid="vendor-backup-close-btn"
            >
              Später bestätigen
            </Button>
            <Button
              onClick={handleConfirmBackup}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="vendor-backup-confirm-btn"
            >
              <CheckCircle2 size={14} className="mr-2" />
              Backup bestätigt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VendorBackupCard;
